import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Trash2, Minimize2 } from 'lucide-react';
import { useChat } from '../context/ChatContext';

// Quick reply suggestions in Vietnamese
const quickReplies = [
  'Quy trình vay mua nhà?',
  'Điểm tín dụng cần bao nhiêu?',
  'Down payment tối thiểu?',
  'FHA vs Conventional?'
];

const ChatbotWidget = () => {
  const { messages, isOpen, isLoading, isStreaming, thinkingStep, toggleChat, sendMessage, clearChat } = useChat();
  const [inputValue, setInputValue] = useState('');
  const [showQuickReplies, setShowQuickReplies] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom when new messages arrive or during streaming
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isStreaming]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Hide quick replies after first user message
  useEffect(() => {
    const hasUserMessage = messages.some(m => m.type === 'user');
    if (hasUserMessage) {
      setShowQuickReplies(false);
    }
  }, [messages]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim() && !isLoading && !isStreaming) {
      sendMessage(inputValue);
      setInputValue('');
    }
  };

  const handleQuickReply = (reply) => {
    sendMessage(reply);
    setShowQuickReplies(false);
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('vi-VN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      {/* Custom styles */}
      <style>{`
        @keyframes chatSlideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes chatPulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }

        @keyframes typingDot {
          0%, 60%, 100% {
            transform: translateY(0);
            opacity: 0.4;
          }
          30% {
            transform: translateY(-4px);
            opacity: 1;
          }
        }

        .chat-window {
          animation: chatSlideUp 0.3s ease-out forwards;
        }

        .chat-button-pulse {
          animation: chatPulse 2s ease-in-out infinite;
        }

        .typing-dot {
          animation: typingDot 1.4s ease-in-out infinite;
        }

        .typing-dot:nth-child(2) {
          animation-delay: 0.2s;
        }

        .typing-dot:nth-child(3) {
          animation-delay: 0.4s;
        }

        .chat-scrollbar::-webkit-scrollbar {
          width: 4px;
        }

        .chat-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }

        .chat-scrollbar::-webkit-scrollbar-thumb {
          background: #475569;
          border-radius: 2px;
        }

        @keyframes blink {
          0%, 50% {
            opacity: 1;
          }
          51%, 100% {
            opacity: 0;
          }
        }

        .streaming-cursor {
          display: inline-block;
          width: 2px;
          height: 14px;
          background-color: #10b981;
          margin-left: 2px;
          animation: blink 1s infinite;
          vertical-align: text-bottom;
        }

        @keyframes thinkingPulse {
          0%, 100% {
            opacity: 0.5;
          }
          50% {
            opacity: 1;
          }
        }

        .thinking-indicator {
          animation: thinkingPulse 1.5s ease-in-out infinite;
        }
      `}</style>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-4 md:right-6 z-50 chat-window">
          <div className="bg-[#0a0f1c] border border-[#1e293b] rounded-2xl shadow-2xl overflow-hidden w-[340px] md:w-[380px] max-h-[500px] flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#10b981] to-[#059669] px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">Trợ Lý Vay Nhà</h3>
                  <p className="text-white/80 text-xs">Nhamy.us</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={clearChat}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                  title="Xóa lịch sử chat"
                >
                  <Trash2 className="w-4 h-4 text-white/80" />
                </button>
                <button
                  onClick={toggleChat}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                  title="Thu nhỏ"
                >
                  <Minimize2 className="w-4 h-4 text-white/80" />
                </button>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#0f1629] chat-scrollbar min-h-[280px] max-h-[320px]">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                      message.type === 'user'
                        ? 'bg-[#10b981] text-white rounded-br-md'
                        : message.isError
                        ? 'bg-red-900/30 text-red-300 border border-red-800/50 rounded-bl-md'
                        : 'bg-[#1e293b] text-gray-100 rounded-bl-md'
                    }`}
                  >
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">
                      {message.content}
                      {message.isStreaming && <span className="streaming-cursor"></span>}
                    </p>
                    {!message.isStreaming && (
                      <p className={`text-[10px] mt-1 ${
                        message.type === 'user' ? 'text-white/60' : 'text-gray-500'
                      }`}>
                        {formatTime(message.timestamp)}
                      </p>
                    )}
                  </div>
                </div>
              ))}

              {/* Thinking Step Indicator */}
              {thinkingStep && (
                <div className="flex justify-start">
                  <div className="bg-[#1e293b]/50 rounded-2xl rounded-bl-md px-4 py-2 thinking-indicator">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-[#10b981] border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-xs text-gray-400">{thinkingStep.title || 'Đang suy nghĩ...'}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Typing Indicator - shown when waiting for API response */}
              {isLoading && !isStreaming && (
                <div className="flex justify-start">
                  <div className="bg-[#1e293b] rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex gap-1.5">
                      <span className="w-2 h-2 bg-gray-400 rounded-full typing-dot"></span>
                      <span className="w-2 h-2 bg-gray-400 rounded-full typing-dot"></span>
                      <span className="w-2 h-2 bg-gray-400 rounded-full typing-dot"></span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Quick Replies */}
            {showQuickReplies && messages.length === 1 && (
              <div className="px-4 py-2 bg-[#0f1629] border-t border-[#1e293b]">
                <p className="text-xs text-gray-500 mb-2">Gợi ý câu hỏi:</p>
                <div className="flex flex-wrap gap-2">
                  {quickReplies.map((reply, index) => (
                    <button
                      key={index}
                      onClick={() => handleQuickReply(reply)}
                      className="text-xs bg-[#1e293b] hover:bg-[#10b981]/20 text-gray-300 hover:text-[#10b981] px-3 py-1.5 rounded-full border border-[#334155] hover:border-[#10b981]/50 transition-all"
                    >
                      {reply}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input Area */}
            <form onSubmit={handleSubmit} className="p-3 bg-[#0a0f1c] border-t border-[#1e293b]">
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Nhập câu hỏi của bạn..."
                  disabled={isLoading || isStreaming}
                  className="flex-1 bg-[#1e293b] text-white text-sm rounded-full px-4 py-2.5 border border-[#334155] focus:border-[#10b981] focus:outline-none focus:ring-1 focus:ring-[#10b981]/50 placeholder-gray-500 disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={!inputValue.trim() || isLoading || isStreaming}
                  className="w-10 h-10 bg-[#10b981] hover:bg-[#059669] disabled:bg-[#1e293b] disabled:cursor-not-allowed rounded-full flex items-center justify-center transition-colors"
                >
                  <Send className="w-4 h-4 text-white" />
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Floating Chat Button */}
      <button
        onClick={toggleChat}
        className={`fixed bottom-6 right-4 md:right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
          isOpen
            ? 'bg-[#1e293b] hover:bg-[#334155]'
            : 'bg-gradient-to-r from-[#10b981] to-[#059669] hover:from-[#059669] hover:to-[#047857] chat-button-pulse'
        }`}
        aria-label={isOpen ? 'Đóng chat' : 'Mở chat'}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </button>

      {/* Tooltip for closed state */}
      {!isOpen && (
        <div className="fixed bottom-[88px] right-4 md:right-6 z-50 bg-[#0a0f1c] text-white text-xs px-3 py-2 rounded-lg shadow-lg border border-[#1e293b] opacity-0 hover:opacity-100 pointer-events-none transition-opacity">
          Chat với Trợ Lý Vay Nhà
        </div>
      )}
    </>
  );
};

export default ChatbotWidget;
